import React, {
  useContext, useEffect, useState, useCallback,
} from 'react';
import {
  Button,
  CardShowcase,
  Heading, Input,
} from '@americanexpress/dls-react';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import Wrapper from '../components/Wrapper';
import DropDown from '../components/DropDown';
import DatePickerCustom from '../components/DatePickerCustom';
import PaymentRadio from '../components/PaymentRadio';
import { BankContext } from '../utility/BankContext';
import { PaymentContext } from '../utility/PaymentContext';
import { AmountSelectedContext } from '../utility/AmountSelectedContext';
import { DateContext } from '../utility/DateContext';
import { TokenContext } from '../utility/TokenContext';
import useApiWithSession from '../utility/useApiWithSession';

// Helper to format date string as local date
function formatLocalDate(dateString) {
  if (!dateString) return 'Invalid Date';
  let date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    date = new Date(`${dateString}T00:00:00`);
  } else {
    date = new Date(dateString);
  }
  return !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(date)
    : 'Invalid Date';
}
// Helper: Convert MM/DD/YYYY to YYYY-MM-DD
const normalizeDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const [mm, dd, yyyy] = dateStr.split('/');
  if (mm && dd && yyyy) return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  return dateStr;
};

// Helper: Parse date string as local midnight if in YYYY-MM-DD format
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(`${dateStr}T00:00:00`);
  }
  return new Date(dateStr);
};

const PayYourBill = ({ router }) => {
  // Contexts
  const dispatch = useDispatch();
  const { gateWayUrl } = useContext(TokenContext);
  const { selectedBank, setSelectedBank } = useContext(BankContext);
  const { setAmountSelected, setPaymentType } = useContext(AmountSelectedContext);
  const { selectedDate, setSelectedDate, formattedDate } = useContext(DateContext);
  const {
    paymentResponse, market1, journey1, sid1, u1,
  } = useContext(PaymentContext);

  const [inputValue, setInputValue] = useState('');
  const [inpActive, setInpActive] = useState(false);
  const [banks, setBanks] = useState([]);
  const [isAmountSelected, setIsAmountSelected] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [paymentValues, setPaymentValues] = useState([]);
  const [selectedRadioValue, setSelectedRadioValue] = useState('');
  const [amountError, setAmountError] = useState('');
  const [dateError, setDateError] = useState('');

  const {
    isLoading: isLoadingWelcome, data: dataWelcome, error: errorWelcome, run: run2,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'WELCOME_OK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'VUI',
      market: market1,
      journey: journey1,
    },
  });
  // API hook for PAYBILL_OK
  const {
    isLoading, data, error, run,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'PAYBILL_OK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });

  // Scheduled remittances and disabled dates
  const scheduledRemittances = paymentResponse?.paymentHistory?.remittances?.filter(
    (remittance) => remittance?.frequency === 'SCHEDULED'
      && remittance?.source === 'PAYBILL'
      && (remittance?.paymentType === 'PAYBILL' || remittance?.program === 'WEB_COLLECTIONS')
      && (remittance?.status === 'ACTIVE' || remittance?.status === 'PROCESSING')
  );
  const disabledScheduledDates = scheduledRemittances?.map(
    (remittance) => remittance?.paymentDate
  ).filter(Boolean);

  // Validate date
  const isValidDate = useCallback((date) => {
    if (!date) return false;
    const normalized = normalizeDate(date);
    if (disabledScheduledDates?.includes(normalized)) return false;
    const d = parseLocalDate(normalized);
    if (Number.isNaN(d.getTime())) return false;
    if (paymentResponse?.paymentOptions?.paymentDueDate) {
      const maxDate = parseLocalDate(paymentResponse?.paymentOptions?.paymentDueDate);
      if (d > maxDate) return false;
    }
    if (formattedDate) {
      const minDate = parseLocalDate(normalizeDate(formattedDate));
      if (d < minDate) return false;
    }
    return true;
  }, [disabledScheduledDates, formattedDate, paymentResponse]);

  useEffect(() => {
    const sendWelcomeOkLoadEvent = async () => {
      try {
        const sessionId = document.cookie
          .split('; ')
          .find((row) => row.startsWith('session_id='))
          ?.split('=')[1];
        if (!sessionId || !paymentResponse) {
          const err = new Error('Session ID is missing or response is missing');
          dispatch(addErrorToReport(err, { message: 'Welcome: Missing session ID or payment response.' }));
          router.push({ pathname: '/message', query: { level: 3 } });
          return;
        }
        await run2();
      } catch (er) {
        dispatch(addErrorToReport(er, { message: 'Welcome: WELCOME_OK event failed.' }));
      }
    };
    sendWelcomeOkLoadEvent();
  }, []);

  useEffect(() => {
    if (isLoadingWelcome === false && (errorWelcome || dataWelcome?.statusCode === 'failure')) {
      dispatch(addErrorToReport(errorWelcome || new Error('WELCOME_OK failed'), { message: 'Welcome: WELCOME_OK event failed.' }));
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [dataWelcome?.statusCode, errorWelcome, isLoadingWelcome, dispatch, router]);

  // Handle API response for PAYBILL_OK
  useEffect(() => {
    if (isLoading === false && !error && data?.statusCode === 'success') {
      router.push({ pathname: '/paymentConfirmation' });
    } else if (isLoading === false && (error || data?.statusCode === 'failure')) {
      dispatch(addErrorToReport(error || new Error('PAYBILL_OK failed'), { message: 'PayYourBill: PAYBILL_OK event failed.' }));
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [isLoading, error, data, dispatch, router]);

  // Initialize banks and payment options
  useEffect(() => {
    const sessionId = document.cookie
      .split('; ')
      .find((row) => row.startsWith('session_id='))
      ?.split('=')[1];
    if (!paymentResponse || !sessionId) {
      router.push({ pathname: '/message', query: { level: 3 } });
      return;
    }
    const filteredActiveBanks = paymentResponse.fundingInstruments.instruments.filter(
      (instrument) => instrument.status === 'ACTIVE'
    );
    setBanks(filteredActiveBanks);
    setSelectedBank(filteredActiveBanks.find((bank) => bank.preferred) || filteredActiveBanks[0]);
    setPaymentValues(paymentResponse.paymentOptions.defaultPaymentOptions);
    setSelectedDate(formattedDate);
  }, [formattedDate, router, setSelectedBank, setSelectedDate, paymentResponse]);

  // Update payment options when bank changes
  useEffect(() => {
    if (selectedBank) {
      const fundingAccountEnrollId = selectedBank.arrangementManagerEnrollmentId;
      const options = paymentResponse.paymentOptions.paymentOptionsByFundingAccount
        .filter((option) => option.fundingAccountEnrollId === fundingAccountEnrollId);
      setPaymentOptions(options);
    }
    setSelectedRadioValue('');
    setIsAmountSelected(false);
    setInpActive(false);
    setAmountSelected('');
    setAmountError('');
  }, [paymentResponse?.paymentOptions?.paymentOptionsByFundingAccount,
    selectedBank, setAmountSelected]);

  // Validate date whenever selectedDate changes
  useEffect(() => {
    setDateError(isValidDate(selectedDate) ? '' : 'Please select a valid date.');
  }, [selectedDate, isValidDate]);

  // Handle browser back button
  useEffect(() => {
    // Block browser back/forward navigation
    const handlePopState = () => {
      window.history.go(1);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handlers
  const changeBalance = (e) => {
    setAmountError('');
    setInputValue('');
    const selectedOption = paymentValues.find((option) => option.name === e.target.value);
    if (selectedOption) {
      setAmountSelected(selectedOption.amount);
      setPaymentType(selectedOption.name);
      setSelectedRadioValue(e.target.value);
      setInpActive(false);
      setIsAmountSelected(true);
    } else if (e.target.value === 'option-4') {
      setInpActive(true);
      setPaymentType('FIXED_AMOUNT');
      setIsAmountSelected(false);
      setSelectedRadioValue('option-4');
    } else {
      setInpActive(false);
      setPaymentType('');
      setAmountSelected('');
      setSelectedRadioValue('');
      setIsAmountSelected(false);
    }
  };

  const onDropDownSelect = (event) => {
    setSelectedBank(JSON.parse(event.target.value));
    setSelectedDate(formattedDate);
    setAmountSelected(0);
    setInputValue('');
    setPaymentType('');
  };

  const handleProceed = async () => {
    try {
      const sessionId = document.cookie
        .split('; ')
        .find((row) => row.startsWith('session_id='))
        ?.split('=')[1];
      if (!sessionId) {
        router.push({ pathname: '/message', query: { level: 3 } });
        return;
      }
      await run();
    } catch (e) {
      // Error handling for API call
    }
  };

  // Input change handler for custom amount
  const handleInputChange = (e) => {
    let { value } = e.target;
    if (value.startsWith('.')) value = `0${value}`;
    const regex = /^(?:\d{1,10}|\d{1,10}\.\d{0,2})$/;
    if (value.trim() === '' || regex.test(value.trim())) {
      setInputValue(value);
      setAmountSelected(value);
      setIsAmountSelected(value.trim() !== '');
      const selected = parseLocalDate(selectedDate);
      const today = parseLocalDate(formattedDate);
      const isFuture = selected && today && selected.getTime() > today.getTime();
      const limit = isFuture
        ? paymentOptions[0]?.maximumSchedulePaymentAmount
        : paymentOptions[0]?.maximumImmediatePaymentAmount;
      if (value && Number.parseFloat(value) > limit) {
        setAmountError(`Amount cannot exceed $${limit} for ${isFuture ? 'scheduled' : 'immediate'} payments.`);
      } else if (value && Number.parseFloat(value) <= 0) {
        setAmountError('Amount must be greater than zero.');
      } else {
        setAmountError('');
      }
    }
  };

  const paymentDueDate = paymentResponse?.paymentOptions?.paymentDueDate;
  return (
    <Wrapper>
      <div className="width-full">
        <Heading level={4} headingStyle="heading4" style={{ fontWeight: 700 }} className="pad-3-t pad-2-l">
          Select Payment Details
        </Heading>
        <CardShowcase className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded">
          <div className="pad-1-lr pad-2-tb card-rounded flex flex-column flex-align-start" style={{ gap: '1rem' }}>
            <div className="flex flex-column width-full text-align-left">
              <Heading headingStyle="heading3" level={3}>Select Bank Account</Heading>
              <DropDown
                id={`select-${1234}`}
                value={JSON.stringify(selectedBank)}
                handleSelection={onDropDownSelect}
                showOptions="show"
                options={banks}
              />
              <p className="heading-3">
                Your payment is due on {formatLocalDate(paymentDueDate)}
              </p>
            </div>
            <div className="flex flex-column width-full text-align-left">
              <Heading headingStyle="heading3" level={3}>Amount</Heading>
              <PaymentRadio
                paymentValues={paymentValues}
                paymentOptions={paymentOptions}
                changeBalance={changeBalance}
                selectedRadioValue={selectedRadioValue}
              />
              {inpActive && (
                <Input
                  value={inputValue}
                  disabled={!inpActive}
                  hint="Hint Text"
                  id="input"
                  label=""
                  placeholder="Enter Amount"
                  statusMessage="Message."
                  type="text"
                  className="width-full anim-fade-in anim-delay-1 anim-slide-down"
                  onChange={handleInputChange}
                />
              )}
              {amountError && (
                <div style={{ color: 'red', marginTop: '0.5rem' }}>{amountError}</div>
              )}
            </div>
            <DatePickerCustom
              startDate={formattedDate}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              endDate={paymentResponse?.paymentOptions?.paymentDueDate}
              disableDates={disabledScheduledDates}
            />
            {dateError && (
              <div style={{ color: 'red', marginTop: '0.5rem' }}>{dateError}</div>
            )}
            <p className="legal-1 text-align-left">
              Keep in mind payments must be submitted by 8:00 PM MST
              to be posted with today&apos;s date.
            </p>
            <Button
              type="button"
              className="width-full"
              styleType="primary"
              style={{ maxWidth: '100%' }}
              disabled={
                !isAmountSelected || !!amountError || !!dateError || !isValidDate(selectedDate)
            }
              onClick={handleProceed}
            >
              <p className="heading-3">Proceed</p>
            </Button>
          </div>
        </CardShowcase>
      </div>
    </Wrapper>
  );
};

export default withRouter(PayYourBill);

PayYourBill.propTypes = {
  router: PropTypes.instanceOf(Object).isRequired,
};
